import React, { useState, useEffect, useCallback } from 'react';
import { getSupabaseClient } from '@/src/lib/supabase';
import { Button } from '@/src/components/ui/button';
import { 
  X, 
  Printer, 
  RefreshCw, 
  FileText, 
  AlertCircle,
  Receipt
} from 'lucide-react';
import { toast } from 'sonner';

interface ServiceVoucherPrintModalProps {
  kind: 'inward' | 'outward' | 'expense' | 'income';
  voucherId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ServiceVoucherPrintModal({
  kind,
  voucherId,
  isOpen,
  onClose
}: ServiceVoucherPrintModalProps) {
  const supabase = getSupabaseClient();

  const [printerSize, setPrinterSize] = useState<'80mm' | 'A4-Half'>('80mm');
  const [loading, setLoading] = useState<boolean>(true);
  const [voucherData, setVoucherData] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchVoucher = useCallback(async () => {
    if (!supabase || !voucherId) return;
    setLoading(true);
    setErrorMsg(null);

    try {
      const { data, error } = await supabase.rpc('get_service_voucher_print', {
        p_kind: kind,
        p_id: voucherId
      });

      if (error) {
        console.error('get_service_voucher_print error:', error);
        setErrorMsg(error.message);
        toast.error('Voucher print error: ' + error.message);
      } else if (data) {
        setVoucherData(data);
      }
    } catch (err: any) {
      console.error('Failed to load voucher print data:', err);
      setErrorMsg(err.message || 'Failed to load voucher');
      toast.error(err.message || 'Failed to load voucher');
    } finally {
      setLoading(false);
    }
  }, [supabase, kind, voucherId]);

  useEffect(() => {
    if (isOpen) {
      fetchVoucher();
    }
  }, [isOpen, fetchVoucher]);

  if (!isOpen) return null;

  const header = voucherData?.header || {};
  const voucher = voucherData?.voucher || {};
  const branding = header?.branding || {};

  const fullAddress = [
    header.address,
    header.city,
    header.state,
    header.pincode
  ].filter(Boolean).join(', ');

  const handlePrint = () => {
    const printContent = document.getElementById('voucher-print-area');
    if (!printContent) return;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const win = iframe.contentWindow;
    if (win) {
      const widthCss = printerSize === '80mm' ? '80mm' : '148mm';
      win.document.open();
      win.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${voucher.number || 'VOUCHER'}</title>
            <style>
              @page { size: ${widthCss} auto; margin: 0; }
              body { margin: 0; padding: 4mm; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; font-size: ${printerSize === '80mm' ? '11px' : '13px'}; color: #111; }
              .text-center { text-align: center; }
              .text-right { text-align: right; }
              .font-bold { font-weight: bold; }
              .border-b { border-bottom: 1px dashed #999; }
              .border-t { border-top: 1px dashed #999; }
              .my-2 { margin-top: 8px; margin-bottom: 8px; }
              .py-1 { padding-top: 4px; padding-bottom: 4px; }
              .flex { display: flex; justify-content: space-between; }
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>
            ${printContent.innerHTML}
          </body>
        </html>
      `);
      win.document.close();
      win.focus();

      setTimeout(() => {
        win.print();
        setTimeout(() => {
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }, 1000);
      }, 300);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4 animate-in fade-in">
      <div className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-950">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-primary/10 text-primary">
              <Receipt size={18} />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white text-base">
                Voucher Print & Receipt
              </h3>
              <p className="text-xs text-slate-500 capitalize">
                {kind} voucher receipt
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchVoucher}
              disabled={loading}
              className="text-xs h-8 gap-1"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </Button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="p-3 border-b border-slate-200 dark:border-slate-800 bg-slate-100/60 dark:bg-slate-900/60 flex items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-slate-600 dark:text-slate-300">Size:</span>
            <select
              value={printerSize}
              onChange={e => setPrinterSize(e.target.value as any)}
              className="h-8 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 font-medium text-xs text-slate-900 dark:text-slate-100 outline-none"
            >
              <option value="80mm">3 Inch (80mm) Thermal Slip</option>
              <option value="A4-Half">A4-Half / A5 Voucher</option>
            </select>
          </div>

          <Button
            onClick={handlePrint}
            disabled={loading || !voucherData}
            className="bg-primary hover:bg-primary/90 text-white text-xs h-8 px-4 font-semibold gap-1.5 shadow-sm"
          >
            <Printer size={13} />
            <span>Print Receipt</span>
          </Button>
        </div>

        {/* Printable Body */}
        <div className="flex-1 overflow-y-auto bg-slate-100 dark:bg-slate-950 p-4 sm:p-6 flex justify-center items-start">
          {loading ? (
            <div className="p-12 text-center text-slate-500 flex flex-col items-center justify-center gap-2">
              <RefreshCw size={24} className="animate-spin text-primary" />
              <span className="text-xs font-medium">Loading voucher receipt...</span>
            </div>
          ) : errorMsg ? (
            <div className="p-6 text-center bg-white dark:bg-slate-900 rounded-xl border border-rose-200 dark:border-rose-900 shadow-sm space-y-2">
              <AlertCircle size={24} className="text-rose-500 mx-auto" />
              <p className="text-xs text-rose-600 dark:text-rose-400 font-medium">{errorMsg}</p>
            </div>
          ) : (
            <div 
              id="voucher-print-area"
              className={`bg-white text-slate-900 shadow-xl border border-slate-200 p-5 rounded-md ${
                printerSize === '80mm' ? 'w-[80mm]' : 'w-[148mm]'
              }`}
            >
              {/* Header */}
              <div className="text-center pb-2 border-b border-dashed border-slate-300 space-y-1">
                {header.logo_url && (
                  <img src={header.logo_url} alt="Logo" className="h-10 mx-auto object-contain mb-1" />
                )}
                <h2 className="text-sm font-bold tracking-tight uppercase">
                  {header.business_name || 'Business Name'}
                </h2>
                {branding.show_branch_name !== false && header.branch_name && (
                  <p className="text-[11px] font-semibold text-slate-600">
                    {header.branch_name}
                  </p>
                )}
                {branding.show_branch_address !== false && fullAddress && (
                  <p className="text-[10px] text-slate-500 leading-tight">
                    {fullAddress}
                  </p>
                )}
              </div>

              {/* Title & Voucher Number */}
              <div className="py-2.5 text-center border-b border-dashed border-slate-300">
                <span className="inline-block px-2.5 py-0.5 rounded text-[11px] font-extrabold uppercase tracking-wider bg-slate-100 text-slate-800">
                  {voucher.title || 'PAYMENT RECEIPT'}
                </span>
                <div className="flex justify-between items-center text-[11px] mt-2 font-mono text-slate-600">
                  <span>No: <strong className="text-slate-900 font-bold">{voucher.number || 'VCH-000'}</strong></span>
                  <span>{voucher.date ? new Date(voucher.date).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}</span>
                </div>
              </div>

              {/* Details Grid */}
              <div className="py-3 space-y-1.5 text-xs border-b border-dashed border-slate-300">
                <div className="flex justify-between">
                  <span className="text-slate-500">{voucher.party_label || 'Party'}:</span>
                  <strong className="text-slate-900 text-right">{voucher.party || 'Cash'}</strong>
                </div>

                {voucher.party_phone && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Phone:</span>
                    <span className="text-slate-700 font-mono">{voucher.party_phone}</span>
                  </div>
                )}

                {voucher.category && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Category:</span>
                    <span className="text-slate-700 font-medium">{voucher.category}</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="text-slate-500">Payment Mode:</span>
                  <span className="font-semibold uppercase text-slate-800">{voucher.method || 'CASH'}</span>
                </div>

                {voucher.invoice_number && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Ref Invoice:</span>
                    <span className="font-mono text-slate-700">{voucher.invoice_number}</span>
                  </div>
                )}

                {voucher.note && (
                  <div className="pt-1 text-[11px] text-slate-500 italic">
                    Note: {voucher.note}
                  </div>
                )}
              </div>

              {/* Amount Highlight */}
              <div className="py-3 text-center border-b border-dashed border-slate-300 bg-slate-50 rounded-lg my-2">
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider block">
                  Amount Received / Paid
                </span>
                <div className="text-xl font-extrabold text-slate-900 font-mono mt-0.5">
                  ₹{Number(voucher.amount || 0).toFixed(2)}
                </div>
              </div>

              {/* Footer / Signature */}
              <div className="pt-4 flex justify-between items-end text-[10px] text-slate-500">
                <div>
                  <span>Thank you!</span>
                </div>
                <div className="text-center space-y-1">
                  {branding.stamp_url && (
                    <img src={branding.stamp_url} alt="Stamp" className="h-10 mx-auto object-contain" />
                  )}
                  <div className="border-t border-slate-400 pt-1 w-28">
                    <span>Authorized Sign</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="text-xs h-8 px-4"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ServiceVoucherPrintModal;
